import { Component, inject } from '@angular/core';
import {
    DxDataGridComponent,
    DxFormComponent,
    DxTabsModule,
    DxTemplateDirective,
} from 'devextreme-angular';
import {
    DxiButtonComponent,
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSearchPanelComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import { FilterPanelComponent } from '../../../components/library/filter-panel/filter-panel.component';
import {
    FilterConfig,
    GridPageComponent,
} from '../../common/grid-page.component';
import {
    AccessType,
    AuthService,
    EmployeeStatus,
    NextViewGridViewDto,
    PermissionType,
    ProductivityReportType,
} from '@services';
import EnumUtils from '../../../shared/enum-utils';
import { CommonModule } from '@angular/common';
import { DATE_TIME_FORMAT } from 'src/app/shared/constants';
import { UserAvatarEmailComponent } from 'src/app/components/utils/user-avatar-email/user-avatar-email.component';
import { NextViewScoreComponent } from './next-view-score/next-view-score.component';

@Component({
    selector: 'app-next-view',
    imports: [
        CommonModule,
        DxDataGridComponent,
        DxiButtonComponent,
        DxoScrollingComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoLoadPanelComponent,
        DxoPositionComponent,
        DxoSortingComponent,
        DxoSearchComponent,
        DxoHeaderFilterComponent,
        DxoFilterRowComponent,
        DxoColumnChooserComponent,
        DxoSearchPanelComponent,
        DxoSelectionComponent,
        DxoExportComponent,
        DxoToolbarComponent,
        DxiItemComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        FilterPanelComponent,
        DxFormComponent,
        UserAvatarEmailComponent,
        NextViewScoreComponent,
        DxTabsModule,
    ],
    templateUrl: './next-view.component.html',
})
export class NextViewComponent extends GridPageComponent<NextViewGridViewDto> {
    private auth = inject(AuthService);
    protected override exportName: string = 'Efficiency Report';
    protected offset: number = 150;
    public dateTimeFormat = DATE_TIME_FORMAT;
    readonly numberFormat = "#0.00";

    dataSource = this.dataSources.nextView();

    productivityReportTypeOptions = EnumUtils.getEnumOptions(ProductivityReportType);
    employeeStatusOptions = EnumUtils.getEnumOptions(EmployeeStatus);

    lastModifiedByFilter = this.userNameOrEmailFilter('lastModifiedBy');
    createdByFilter = this.userNameOrEmailFilter('createdBy');

    protected override filterConfig: FilterConfig[] = [
        { field: 'date', op: '>=' },
        { field: 'positionId', op: 'contains' },
        { field: 'status', op: '=' },
        { field: 'shift', op: 'contains' },
        { field: 'type', op: '=' },
        { field: 'stdTotal', op: '=' },
        { field: 'stdHours', op: '=' },
        { field: 'efficiency', op: '=' },
        {
            field: 'lastModifiedBy.name',
            op: 'contains',
            customFilter: this.lastModifiedByFilter,
        },
        { field: 'lastModified', op: '>=' },
        {
            field: 'createdBy.name',
            op: 'contains',
            customFilter: this.createdByFilter,
        },
        { field: 'created', op: '>=' },
    ];

    tabs = [
        { text: 'My Score', id: 0 },
        { text: 'Data', id: 1 }
    ];

    selectedTab = this.tabs[0];

    constructor() {
        super();
        this.init(NextViewGridViewDto);
    }

    onTabClick(e: any) {
        this.selectedTab = e.itemData;
    }

    employeeCellValue = (data: NextViewGridViewDto): string | undefined => {
        return data.employeeName || data.employee?.name;
    };

    employeeFilter = (filterValue: any, selectedFilterOperation: string) => {
        if (!filterValue) {
            return null;
        }

        return [
            [
                'employeeName',
                selectedFilterOperation || 'contains',
                filterValue,
            ],
            'or',
            [
                'employee.name',
                selectedFilterOperation || 'contains',
                filterValue,
            ],
            'or',
            [
                'employee.email',
                selectedFilterOperation || 'contains',
                filterValue,
            ],
        ];
    };

    protected get canDelete(): boolean {
        return this.auth.checkPermission(PermissionType.DeleteProductivityReport, AccessType.Manage);;
    };

    protected onDelete = ($event: any) => {
        this.dataGrid.instance.deleteRow($event.row.dataIndex);
    };

    protected getBadgeClass(status: EmployeeStatus | undefined): string {
        if (status === EmployeeStatus.Active) {
            return 'bg-success-subtle text-success';
        }
        else {
            return 'bg-danger-subtle text-danger';
        }
    }
}
